import history from '../../history'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppLayout } from '../../App/Layout/AppLayout'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveNavigationTab } from '../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../types/navigation'
import {
  Box,
  Flex,
  Text,
  Grid,
  Heading,
  useTheme,
  Icon,
  Switch,
  FormLabel,
  FormControl,
  useToast,
  Link as ChakraLink
} from '@homebotapp/hb-react-component-catalog'
import { ListingCardNew } from '../../Components/homeSearch/ListingCardNew/ListingCardNew'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Link as ReactRouterLink } from 'react-router-dom'
import {
  ClientEventName,
  ClientEventSource,
  FeedType,
  ListingCompact,
  MarketSource
} from '../../api/gqlaxy/gql/generated/graphql'
import { useGetFeed } from '../../hooks/gallery/useFeed'
import { CurrentSelectedLocation } from '../../Components/homeSearch/GoogleAutoComplete/state/currentSelectedLocation'
import { PlaceDetailsCompat } from '../../hooks'
import { GallerySubscribeModal } from '../../Components/homeSearch/GallerySubscribeModal/GallerySubscribeModal'
import { useToggleFeedSubscribed } from '../../hooks/gallery/useToggleFeedSubscribed'
import { HomeSearchFooter } from '../../Components/homeSearch/HomeSearchFooter/HomeSearchFooter'
import { selectHomebotListingsFeatureEnabled, selectNativeAccessFeature } from '../../store/selectors/customerProfile'
import { RootState } from '../../types/rootState'
import useViewport from '../../hooks/useViewport'
import { DownloadMobileAppPromptCard } from '../../Components/homeSearch/ListingDetails/DownloadMobileAppPromptCard/Desktop/DownloadMobileAppPromptCard'
import { MobileDownloadMobileAppPromptCard } from '../../Components/homeSearch/ListingDetails/DownloadMobileAppPromptCard/Mobile/MobileDownloadMobileAppPromptCard'
import { hasCustomerToken, isAuthTokenSupported } from '../../auth'
import { fetchProfile } from '../../actions/customerProfile'
import { fetchClientData, fetchClientHomes, setCurrentClient } from '../../actions/client'
import { NoPreviewModeModal } from '../../Components/NoPreviewModeModal/NoPreviewModeModal'
import { SEARCH_PATH_HELPERS } from '../../constants/navigation'
import { useEffectOncePerSessionConditionally, useCreateClientEvent } from '../../hooks'
import { AssumablesDirectMessage } from '../../Components/homeSearch/AssumablesDirectMessage/AssumablesDirectMessage'
import { AssumablesLearnMoreModal } from '../../Components/homeSearch/AssumablesLearnMoreModal/AssumablesLearnMoreModal'
import { GenericFeedDirectMessage } from '../../Components/homeSearch/GenericFeedDirectMessage/GenericFeedDirectMessage'
import { BuyerPerksLearnMoreModal } from '../../Components/homeSearch/BuyerPerksLearnMoreModal/BuyerPerksLearnMoreModal'
import { FeedEmptyState } from '../../Components/FeedEmptyState/FeedEmptyState'
import { HOME_SEARCH_FOR_YOU_INDIVIDUAL_GALLERY_PAGE } from '../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { useGetClientId } from '../../hooks/useGetClientId'
import { GenericVerificationDirectMessage } from '../../Components/homeSearch/GenericVerificationDirectMessage/GenericVerificationDirectMessage'
import { useVerificationFlowEnabled } from '../../hooks/useVerificationFlowEnabled'
import { CUSTOMER_MESSAGES, TopicKey } from '../../constants/customerMessages'
import { MessageTopicKey } from '../../api/gqlaxy/gql/generated/graphql'
import { MarketSwitchDropdown } from '../ForYouContent/components/MarketSwitchDropdown'
import { CurrentlySelectedZipCityState } from '../ForYouContent/ForYouContent'
import { GoogleAutoComplete } from '../../Components/homeSearch/GoogleAutoComplete/GoogleAutoComplete'
import { useGetMarketInterest } from '../../hooks/gallery/useGetMarketInterest'
import { Spinner } from '../../Components/shared/Navbar/Spinner'
import { useCreateMarketInterest } from '../../hooks/gallery/useCreateMarketInterest'

export const MSG = defineMessages({
  back: {
    id: 'Gallery.back',
    defaultMessage: 'Back'
  },
  noListingFound: {
    id: 'General.noListingFound',
    defaultMessage: 'No listings found'
  },
  subscribe: {
    id: 'Gallery.subscribe',
    defaultMessage: 'Alerts off'
  },
  subscribed: {
    id: 'Gallery.subscribed',
    defaultMessage: 'Alerts on'
  },
  homes: {
    id: 'Gallery.homes',
    defaultMessage: 'Homes'
  },
  updatedToday: {
    id: 'Gallery.updatedToday',
    defaultMessage: 'Updated today'
  },
  recentPriceDrop: {
    id: 'Gallery.priceDrops',
    defaultMessage: 'Price Drops'
  },
  recentPriceDropDescription: {
    id: 'ForYouGallery.priceDropsDescription',
    defaultMessage: 'Homes with a reduced asking price'
  },
  favorites: {
    id: 'Gallery.favorites',
    defaultMessage: 'Favorites'
  },
  concessions: {
    id: 'Gallery.concessions',
    defaultMessage: 'Seller Concessions'
  },
  concessionsDescription: {
    id: 'ForYouGallery.concessionsDescription',
    defaultMessage: 'Homes where the seller is offering incentives'
  },
  recentlySold: {
    id: 'Gallery.recentlySold',
    defaultMessage: 'Recently Sold'
  },
  recentlySoldDescription: {
    id: 'ForYouGallery.recentlySoldDescription',
    defaultMessage: 'Explore to see what the market is like'
  },
  assumables: {
    id: 'Gallery.assumableLoans',
    defaultMessage: 'Assumable Loans'
  },
  assumablesDescription: {
    id: 'Gallery.assumablesDescription',
    defaultMessage: 'You may be able to take on a low-rate loan!'
  },
  newlyListed: {
    id: 'Gallery.newlyListed',
    defaultMessage: 'Newly Listed'
  },
  newlyListedDescription: {
    id: 'Gallery.newlyListedDescription',
    defaultMessage: 'Homes that are just hitting the market'
  },
  openHouses: {
    id: 'Gallery.openHouses',
    defaultMessage: 'Open Houses'
  },
  openHousesDescription: {
    id: 'Gallery.openHousesDescription',
    defaultMessage: 'See homes in person'
  },
  localGallery: {
    id: 'Gallery.localGallery',
    defaultMessage: 'Local Gallery'
  },
  forYou: {
    id: 'Gallery.forYou',
    defaultMessage: 'For You'
  },
  help: {
    id: 'ForYouGallery.help',
    defaultMessage: "Let's find your next home"
  },
  localGalleries: {
    id: 'Gallery.localGalleries',
    defaultMessage: 'Local Galleries'
  },
  changeLocation: {
    id: 'Gallery.changeLocation',
    defaultMessage: 'Change Location'
  },
  generalGalleriesTopic: {
    id: 'Gallery.generalGalleriesTopic',
    defaultMessage: 'listing galleries'
  },
  loadingYourMarkets: {
    id: 'ForYouGallery.loadingYourMarkets',
    defaultMessage: 'Loading your markets'
  },
  enterZipCode: {
    id: 'ForYouGallery.enterZipCode',
    defaultMessage: 'Enter ZIP code'
  },
  zipCodeAlreadyAddedToFavorites: {
    id: 'ForYouGallery.zipCodeAlreadyAddedToFavorites',
    defaultMessage: 'Zipcode is already added to your favorites.'
  },
  zipCodeCantBeAdded: {
    id: 'ForYouGallery.zipCodeCantBeAdded',
    defaultMessage: 'This ZIP code cannot be added to your favorites at this time.'
  },
  seeOnMap: {
    id: 'ForYouGallery.seeOnMap',
    defaultMessage: 'See on map'
  }
})

export const ForYouGallery = ({ match }) => {
  const { theme } = useTheme()
  const isImpersonating = hasCustomerToken()
  const dispatch = useDispatch()
  const intl = useIntl()
  const { isMediumOrAbove } = useViewport()
  const hasNativeAccess = useSelector<RootState, boolean>(selectNativeAccessFeature)
  const { customerProfileId, galleryId } = match.params
  const [showGooglePlaceSelector, setShowGooglePlaceSelector] = useState(false)
  const toast = useToast()
  const { data, refetch, isLoading: loadingFeed } = useGetFeed({ feedId: galleryId })
  const [showSubscribedModal, setShowSubscribedModal] = useState(false)
  const searchEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  const clientId = useGetClientId()
  const verificationFlowEnabled = useVerificationFlowEnabled()

  const [showNotAuthorisedModal, setShowNotAuthorisedModal] = useState(false)

  const { mutateAsync: toggleSub, isLoading: togglingSub } = useToggleFeedSubscribed()

  const onShowGooglePlaceSelector = () => {
    if (isImpersonating) {
      toast({
        description: 'Changing gallery locations on behalf of your clients is not supported at this time.',
        status: 'warning'
      })
    } else {
      setShowGooglePlaceSelector(true)
    }
  }

  useEffect(() => {
    if (!isAuthTokenSupported()) {
      return setShowNotAuthorisedModal(true)
    }
    if (customerProfileId) {
      dispatch(fetchProfile(customerProfileId))
    }
  }, [dispatch, customerProfileId])

  useEffect(() => {
    if (clientId) {
      dispatch(setCurrentClient(clientId))
    }
    dispatch(fetchClientData(clientId, customerProfileId))
    dispatch(fetchClientHomes(clientId))
  }, [dispatch, clientId, customerProfileId])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.FORYOU))
  }, [dispatch])

  // --------- Create gallery viewed event START

  const { mutate: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })
  const { mutateAsync: createMarketInterest } = useCreateMarketInterest()

  const feedType = data?.feed.feedType
  const isAssumableFeed = feedType === FeedType.Assumables
  const isNewlyListedFeed = feedType === FeedType.NewlyListed

  // having undefined in key is fine, useEffectOncePerSessionConditionally() will do nothing
  const createClientEventEffectKey = `GalleryViewed_${feedType}`
  const sessionStorageViewedKey = `View_${feedType}`

  const { data: marketsInterest, refetch: refetchMarketsInterest } = useGetMarketInterest({
    enabled: true
  })

  const clientZipCodes = useMemo(() => {
    if (marketsInterest?.markets) {
      return marketsInterest.markets
        .filter(item => item.source === MarketSource.MarketInterest)
        .map(market => market.geoArea.zipCode)
    }
    return []
  }, [marketsInterest?.markets])

  // deprecated, moving to single "viewed" client event
  const createGalleryViewedEvent = useCallback(() => {
    if (!feedType) {
      return
    }

    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.GalleryViewed,
        feedType
      }
    })
  }, [feedType, createClientEvent])

  const createGenericViewEvent = useCallback(() => {
    if (!feedType) {
      return
    }

    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.View,
        eventSource: ClientEventSource.Gallery,
        properties: {
          feed_type: feedType
        }
      }
    })
  }, [feedType, createClientEvent])

  useEffectOncePerSessionConditionally(createGalleryViewedEvent, createClientEventEffectKey, Boolean(feedType))
  useEffectOncePerSessionConditionally(createGenericViewEvent, sessionStorageViewedKey, Boolean(feedType))

  // --------- Create gallery viewed event END

  const toggleSubscription = async () => {
    if (!isAuthTokenSupported()) {
      return
    }
    await toggleSub({ feedId: galleryId })
    await refetch()
    if (data?.feed.subscribed) {
      return
    }
    setShowSubscribedModal(true)
  }

  const showPageLevelLoader = useMemo(() => {
    return loadingFeed || !searchEnabled
  }, [loadingFeed, searchEnabled])

  const getTopicString = (feedType: FeedType) => {
    if (!MSG[feedType]) {
      return null
    }

    return intl.formatMessage(MSG[feedType] || MSG.generalGalleriesTopic).toLowerCase()
  }

  const formatFeedTypeForMetadata = (feedType: FeedType) => {
    if (!feedType) return null

    return String(feedType)
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim()
  }

  const renderDirectMessageComponent = () => {
    const zipcode = data?.feed?.location?.zipCode
    const galleryType = data?.feed?.feedType ? formatFeedTypeForMetadata(data.feed.feedType) : null

    const metadata = {
      ...(zipcode && { zipcode }),
      ...(galleryType && { galleryType })
    }

    if (isAssumableFeed) {
      // Assumable loans gallery - use LoanOfficer verification flow if enabled
      return verificationFlowEnabled ? (
        <GenericVerificationDirectMessage
          defaultTopicKey={MessageTopicKey.BuyerGeneralInquiryToCustomer as TopicKey}
          verifiedTopicKey={CUSTOMER_MESSAGES.HBN_FOR_YOU_GALLERY_LOAN_OFFICER.topicKey}
          message='Can you tell me more about this listing gallery?'
          includePrequal={true}
          showModal={true}
          partnerType='LoanOfficer'
          metadata={metadata}
          buttonProps={{
            width: ['100%', 'auto'],
            px: '16px',
            tracking: {
              guid: 'ForYouGallery_LoanOfficer_VerificationDM',
              ui_context: 'For You Gallery - Finance Flow',
              descriptor: 'Verification flow inquiry about assumable loans gallery'
            }
          }}
        />
      ) : (
        <AssumablesDirectMessage zipcode={metadata.zipcode} />
      )
    }

    // Other galleries - use RealEstateAgent verification flow if enabled
    return verificationFlowEnabled ? (
      <GenericVerificationDirectMessage
        defaultTopicKey={MessageTopicKey.BuyerGeneralInquiryToCustomer as TopicKey}
        verifiedTopicKey={CUSTOMER_MESSAGES.HBN_FOR_YOU_GALLERY_REAL_ESTATE_AGENT.topicKey}
        message='Can you tell me more about this listing gallery?'
        includePrequal={false}
        showModal={true}
        partnerType='RealEstateAgent'
        metadata={metadata}
        buttonProps={{
          px: '16px',
          tracking: {
            guid: 'ForYouGallery_RealEstateAgent_VerificationDM',
            ui_context: 'For You Gallery - Real Estate Flow',
            descriptor: 'Verification flow inquiry about gallery listings'
          }
        }}
      />
    ) : (
      <GenericFeedDirectMessage topic={getTopicString(data?.feed?.feedType)} />
    )
  }

  const learnMoreAssumableButtonProps = useMemo(() => {
    return {
      variant: 'outline',
      bg: 'neutral.50',
      size: ['sm', 'md'],
      tracking: {
        guid: 'eHoqrJtgC74fcanmgJqeCQ',
        ui_context: 'Assumables Gallery Feed',
        descriptor: 'Info button to learn more about assumables'
      }
    }
  }, [])

  const onMarketChange = (zipCityState: CurrentlySelectedZipCityState) => {
    location.replace(
      SEARCH_PATH_HELPERS.forYouTab.buildPathWithQueryParams(
        customerProfileId,
        clientId,
        `zip_code=${zipCityState.zipcode}&feed_type=${feedType}`
      )
    )
  }

  const onCreateMarketInterest = async (zipCode: string) => {
    await createMarketInterest({
      input: {
        clientId,
        zipCode
      }
    })
    refetchMarketsInterest()
  }

  const onLocationAdded = async (place: CurrentSelectedLocation, placeDetails?: PlaceDetailsCompat | null) => {
    try {
      if (!isAuthTokenSupported()) {
        return
      }

      const addressComponents = placeDetails?.address_components
      if (!addressComponents) {
        return
      }

      const zipCode = addressComponents.find(component => component.types.includes('postal_code'))?.long_name
      if (!zipCode) {
        return
      }

      if (clientZipCodes.includes(zipCode)) {
        toast({
          description: intl.formatMessage(MSG.zipCodeAlreadyAddedToFavorites),
          status: 'warning'
        })
        return
      }

      try {
        await onCreateMarketInterest(zipCode)
        location.replace(
          SEARCH_PATH_HELPERS.forYouTab.buildPathWithQueryParams(
            customerProfileId,
            clientId,
            `zip_code=${zipCode}&feed_type=${feedType}`
          )
        )
      } catch (err) {
        toast({
          description: intl.formatMessage(MSG.zipCodeCantBeAdded),
          status: 'warning'
        })
        return
      }
    } catch (err) {
      console.error(err)
    }
  }

  const seeOnMapLink =
    feedType === FeedType.NewlyListed ? (
      <ChakraLink
        display='flex'
        border='1px solid'
        borderColor='neutral.200'
        borderRadius='md'
        bg={['neutral.50', 'transparent']}
        p={['8px', '8px']}
        width={['100%', 'auto']}
        alignItems='center'
        fontWeight='semibold'
        as={ReactRouterLink}
        to={SEARCH_PATH_HELPERS.search.buildPath(customerProfileId)}
        size='m'
      >
        <Icon mr={2} name='map-marked-alt-filled' />
        {intl.formatMessage(MSG.seeOnMap)}
      </ChakraLink>
    ) : null

  const learnMoreCta = useMemo(() => {
    return (
      <>
        {isAssumableFeed ? (
          <AssumablesLearnMoreModal buttonProps={learnMoreAssumableButtonProps} zipcode={data?.feed?.location?.zipCode} />
        ) : data?.feed?.feedType === FeedType.Concessions ? (
          <BuyerPerksLearnMoreModal feedType={data?.feed?.feedType} zipcode={data?.feed?.location?.zipCode} />
        ) : null}
      </>
    )
  }, [isAssumableFeed, learnMoreAssumableButtonProps, data?.feed?.feedType, data?.feed?.location?.zipCode])

  const zipcodeInMarketsInterestId = !!useMemo(() => {
    return marketsInterest?.markets?.find(market => market.geoArea.zipCode === data?.feed?.location?.zipCode)
  }, [marketsInterest?.markets, data?.feed?.location?.zipCode])?.id

  useEffectOncePerSessionConditionally(
    () => {
      ;(async () => {
        try {
          if (!zipcodeInMarketsInterestId && data?.feed?.location?.zipCode) {
            await onCreateMarketInterest(data?.feed?.location?.zipCode)
          }
        } catch (error) {
          console.error(error)
        }
      })()
    },
    'createMarketInterest_useEffectOncePerSessionConditionally_key',
    !zipcodeInMarketsInterestId && !!data?.feed?.location?.zipCode && !!clientId
  )
  return (
    <AppLayout
      loadingMessage={intl.formatMessage(MSG.loadingYourMarkets)}
      loading={showPageLevelLoader}
      enableAgentDropdown
      match={match}
    >
      {showGooglePlaceSelector && (
        <GoogleAutoComplete
          placeholder={intl.formatMessage(MSG.enterZipCode)}
          types={['postal_code', 'administrative_area_level_1']}
          onClose={() => setShowGooglePlaceSelector(false)}
          onPlaceSelected={onLocationAdded}
          returnPlaceDetails={true}
        />
      )}
      {showNotAuthorisedModal && <NoPreviewModeModal />}
      {showSubscribedModal && (
        <GallerySubscribeModal
          feedCategory={data?.feed.feedType as FeedType}
          onClose={() => {
            setShowSubscribedModal(false)
          }}
        />
      )}

      <Box>
        <Box
          mt={-2}
          pt={10}
          px={[5, 20]}
          backgroundSize='cover'
          backgroundBlendMode='multiply'
          backgroundColor='whiteAlpha.300'
          _dark={{
            backgroundColor: 'blackAlpha.400'
          }}
        >
          <Flex
            gap='8px'
            mb='16px'
            alignItems='center'
            color='neutral.900'
            borderBottom={['1px solid', 'none']}
            borderColor='neutral.300'
          >
            <Text
              cursor='pointer'
              onClick={() => {
                history.push(SEARCH_PATH_HELPERS.forYouTab.buildPath(customerProfileId, clientId))
              }}
              fontWeight='bold'
              size='md'
            >
              {intl.formatMessage(MSG.forYou)}
            </Text>
            <Text size='sm'>{'>'}</Text>
            <Text fontWeight='bold' size='md' color='neutral.900'>
              {data?.feed?.feedType && MSG[data.feed.feedType] ? intl.formatMessage(MSG[data.feed.feedType]) : ''}
            </Text>
          </Flex>
          <Flex pb={6} alignItems='center' justifyContent='space-between'>
            <Box width='100%'>
              <Flex width='100%' alignItems='center' gap={4} justifyContent='space-between'>
                <Flex flex={1}>
                  <Heading as='h1' m={0} size='xl' textAlign='left' fontWeight={600}>
                    {data?.feed?.feedType && MSG[data.feed.feedType] ? intl.formatMessage(MSG[data.feed.feedType]) : ''}
                  </Heading>
                </Flex>
                {!isMediumOrAbove && <Flex>{learnMoreCta}</Flex>}
              </Flex>
              <Flex alignItems='center' gap={4}>
                <Text m={0} size='sm'>
                  {data?.feed?.feedType
                    ? intl.formatMessage(MSG?.[`${data?.feed?.feedType}Description`] || MSG.help)
                    : ''}
                </Text>
                {isMediumOrAbove && <Box>{learnMoreCta}</Box>}
              </Flex>
            </Box>
          </Flex>

          <Flex
            alignItems='center'
            justifyContent='space-between'
            gap={4}
            flexDirection={['column', 'column', 'column', 'row']}
          >
            {zipcodeInMarketsInterestId && (
              <MarketSwitchDropdown
                selectedZip={data?.feed?.location?.zipCode}
                setShowGooglePlaceSelector={onShowGooglePlaceSelector}
                onLocationChange={onMarketChange}
              />
            )}

            {data?.feed && (
              <Flex alignItems='flex-end' justifyContent='right' mb={6} flexDir='column' gap={3} w='100%'>
                <Box width={['100%', '100%', '100%', 'auto']}>
                  {isMediumOrAbove && (
                    <Box mb={2} w='100%'>
                      {renderDirectMessageComponent()}
                    </Box>
                  )}

                  {!isImpersonating && (
                    <Flex alignItems='center' gap={2} justifyContent='end' p='0' m='0'>
                      <FormControl
                        justifyContent={['left', 'right']}
                        alignItems='center'
                        width={['100%', '100%', '100%', 'auto']}
                        as={Flex}
                        flex={[1, 'unset']}
                        border='1px solid'
                        borderColor='neutral.200'
                        borderRadius='md'
                        pr='16px'
                      >
                        <Switch
                          m='0'
                          p='8px'
                          gap='8px'
                          tracking={{
                            guid: '6NU8QA8FBp0plpo30PgLSUB',
                            ui_context: `${data.feed.feedType} Gallery Feed Page`,
                            descriptor: data.feed.subscribed
                              ? `Subscribe to ${data.feed.feedType} feed`
                              : `Unsubscribe from ${data.feed.feedType} feed`
                          }}
                          onChange={toggleSubscription}
                          isChecked={data.feed.subscribed}
                          id='subscribe-switch'
                          name='subscribe-switch'
                        />
                        <FormLabel m={0} p={0} htmlFor='unsubscribe'>
                          {togglingSub && <Spinner />}
                          {!togglingSub && (
                            <>
                              {data.feed.subscribed
                                ? intl.formatMessage(MSG.subscribed)
                                : intl.formatMessage(MSG.subscribe)}
                            </>
                          )}
                        </FormLabel>
                      </FormControl>
                      {!isMediumOrAbove && seeOnMapLink && <Flex flex={1}>{seeOnMapLink}</Flex>}
                    </Flex>
                  )}
                </Box>
              </Flex>
            )}
          </Flex>
        </Box>

        <Box my={6} px={[5, 20]}>
          <Flex alignItems='center' justifyContent='space-between'>
            <Text as='span' m={0} mb={1} mr={1} fontWeight={600} size='17px' color='neutral.900'>
              {data?.feed?.listings.length}{' '}
              <FormattedMessage id={MSG.homes.id} defaultMessage={MSG.homes.defaultMessage} />
            </Text>
            {isMediumOrAbove && <Box>{seeOnMapLink}</Box>}
          </Flex>
          <Text as='span' mb={1} size='2xs' textColor='neutral.500' fontStyle='italic'>
            (<FormattedMessage id={MSG.updatedToday.id} defaultMessage={MSG.updatedToday.defaultMessage} />)
          </Text>
        </Box>

        {data?.feed?.listings && data.feed.listings.length > 0 ? (
          <Grid
            templateColumns={['repeat(1, 1fr)', null, 'repeat(2, 1fr)', null, 'repeat(4, 1fr)']}
            gap={4}
            px={[5, 20]}
          >
            {/* Insert renderDirectMessageComponent() at index 1 */}
            {data?.feed?.listings.map((listing, index) => (
              <>
                {index === 1 && !isMediumOrAbove && renderDirectMessageComponent()}
                <ListingCardNew
                  key={listing?.id}
                  listing={listing as ListingCompact}
                  tagPreference={isAssumableFeed ? 'assumableRate' : undefined}
                  tracking={{
                    guid: '2mGadFxrh1Z5mLgnARCqPw',
                    ui_context: HOME_SEARCH_FOR_YOU_INDIVIDUAL_GALLERY_PAGE,
                    descriptor: 'Individual Gallery Listing Clicked'
                  }}
                />
              </>
            ))}
          </Grid>
        ) : (
          <Box p={10}>
            <FeedEmptyState feedType={feedType} />
          </Box>
        )}
      </Box>

      {!hasNativeAccess && (
        <Box margin={8}>
          {isMediumOrAbove ? (
            <DownloadMobileAppPromptCard isLandingPage={true} />
          ) : (
            <MobileDownloadMobileAppPromptCard isLandingPage={true} />
          )}
        </Box>
      )}

      <HomeSearchFooter />
    </AppLayout>
  )
}
